/**
 * Help Center JavaScript
 * ヘルプセンターの検索とアコーディオン機能
 */

class HelpCenterManager {
  constructor() {
    this.searchInput = null;
    this.categories = [];
    this.items = [];
    this.noResultsMessage = null;
    this.searchTimeout = null;
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupElements();
      this.attachEventListeners();
      this.initializeAccessibility();
    });
  }

  setupElements() {
    // 要素の取得
    this.searchInput = document.querySelector('.vf-help__search-input');
    this.categories = Array.from(document.querySelectorAll('.vf-help__category'));
    this.noResultsMessage = document.querySelector('.vf-help__no-results');

    // すべてのアコーディオンアイテムを取得
    this.items = Array.from(document.querySelectorAll('.vf-help__item'));
  }

  attachEventListeners() {
    // 検索イベント
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearch());
      this.searchInput.addEventListener('keydown', (e) => this.handleSearchKeydown(e));
    }

    // アコーディオンボタンのイベント
    this.items.forEach(item => {
      const button = item.querySelector('.vf-help__button');
      if (button) {
        button.addEventListener('click', () => this.toggleAccordion(item));
        button.addEventListener('keydown', (e) => this.handleAccordionKeydown(e, item));
      }
    });
  }

  initializeAccessibility() {
    // ARIA属性の初期設定
    this.items.forEach(item => {
      const button = item.querySelector('.vf-help__button');
      const content = item.querySelector('.vf-help__content');

      if (button && content) {
        const contentId = `help-content-${Math.random().toString(36).substr(2, 9)}`;
        content.setAttribute('id', contentId);
        button.setAttribute('aria-controls', contentId);
        button.setAttribute('aria-expanded', 'false');
      }
    });
  }

  handleSearch() {
    // デバウンス処理
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.performSearch(), 300);
  }

  performSearch() {
    const query = this.normalizeText(this.searchInput.value.trim());
    let anyVisible = false;

    // 各カテゴリーとアイテムをフィルタリング
    this.categories.forEach(category => {
      const categoryItems = Array.from(category.querySelectorAll('.vf-help__item'));
      let categoryHasVisibleItems = false;

      categoryItems.forEach(item => {
        const button = item.querySelector('.vf-help__button');
        const content = item.querySelector('.vf-help__content-inner');

        if (!button || !content) return;

        const buttonText = this.normalizeText(button.textContent);
        const contentText = this.normalizeText(content.textContent);
        const isMatch = !query || buttonText.includes(query) || contentText.includes(query);

        if (isMatch) {
          item.classList.remove('vf-help__item--hidden');
          categoryHasVisibleItems = true;

          // 検索キーワードのハイライト
          if (query) {
            this.highlightText(button, query);
          } else {
            this.removeHighlight(button);
          }
        } else {
          item.classList.add('vf-help__item--hidden');
        }
      });

      // カテゴリーの表示/非表示
      if (categoryHasVisibleItems) {
        category.classList.remove('vf-help__category--hidden');
        anyVisible = true;
      } else {
        category.classList.add('vf-help__category--hidden');
      }
    });

    // 検索結果なしメッセージの表示/非表示
    if (this.noResultsMessage) {
      if (anyVisible || !query) {
        this.noResultsMessage.classList.remove('vf-help__no-results--visible');
      } else {
        this.noResultsMessage.classList.add('vf-help__no-results--visible');
      }
    }
  }

  highlightText(element, query) {
    const buttonText = element.querySelector('.vf-help__button-text');
    if (!buttonText) return;

    const originalText = buttonText.textContent;
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    const highlightedText = originalText.replace(regex, '<span class="vf-help__highlight">$1</span>');
    buttonText.innerHTML = highlightedText;
  }

  removeHighlight(element) {
    const buttonText = element.querySelector('.vf-help__button-text');
    if (buttonText && buttonText.innerHTML !== buttonText.textContent) {
      buttonText.innerHTML = buttonText.textContent;
    }
  }

  toggleAccordion(item) {
    const isOpen = item.classList.contains('vf-help__item--open');
    const button = item.querySelector('.vf-help__button');
    const content = item.querySelector('.vf-help__content');

    if (isOpen) {
      // 閉じる
      item.classList.remove('vf-help__item--open');
      button.setAttribute('aria-expanded', 'false');
      this.slideUp(content);
    } else {
      // 開く
      item.classList.add('vf-help__item--open');
      button.setAttribute('aria-expanded', 'true');
      this.slideDown(content);
    }
  }

  slideDown(element) {
    element.style.maxHeight = element.scrollHeight + 'px';
  }

  slideUp(element) {
    element.style.maxHeight = '0';
  }

  handleSearchKeydown(e) {
    // Escapeキーで検索をクリア
    if (e.key === 'Escape') {
      this.searchInput.value = '';
      this.performSearch();
    }
  }

  handleAccordionKeydown(e, item) {
    // スペースキーまたはEnterキーでトグル
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.toggleAccordion(item);
    }
  }

  normalizeText(text) {
    return (text || '').toLowerCase().replace(/\s+/g, ' ').trim();
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// インスタンス化
new HelpCenterManager();